// ---------------------------------------------------------------------------
// KBHeader — self-contained replacement for Kadence's kadence-header-block.js
// so the Kadence script can be dequeued.
//
// Changes from the original Kadence source:
//  - Uses a content-based breakpoint (.entry-content.single-content > *:nth-child(2)
//    minus 300px) instead of the default anchor-based threshold.
//  - Fixes the (void 0).classList crash in the updateSticky bail-out path.
//  - Fixes the spurious .bind(this) on the respondToVisibility callback.
//  - Swaps transparent / sticky header images on state change.
//  - Falls back to sensible defaults when kadenceHeaderConfig is unavailable.
// ---------------------------------------------------------------------------

const DEFAULT_HEADER_CONFIG = {
  breakPoints: {
    desktop: 1024,
    tablet: 768,
  },
};

function getHeaderConfig() {
  return typeof kadenceHeaderConfig !== 'undefined' ? kadenceHeaderConfig : DEFAULT_HEADER_CONFIG;
}

class KBHeader {
  components = {};
  _state;
  root;
  rootID;
  autoTransparentSpacing;
  sticky;
  stickyTablet;
  stickyMobile;
  transparent;
  transparentTablet;
  transparentMobile;
  stickySection;
  stickySectionTablet;
  stickySectionMobile;
  activeSize = "mobile";
  lastScrollTop = 0;
  activeOffsetTop = 0;
  shrinkMain = false;
  shrinkMainHeight = 0;
  shrinkMainHeightTablet = 0;
  shrinkMainHeightMobile = 0;
  shrinkStartHeight = 0;
  mobileBreakpoint = 0;
  currentTopPosition = 0;
  anchorOffset = 0;
  placeholderWrapper;
  stickyWrapper;
  isSticking = false;
  isTransparent = false;
  activeHeaderContainer;

  // Cached DOM references (set once, reused every frame)
  _cachedSiteBlocks = null;
  _cachedBreakpointEl = null;
  _cachedAdminNav = null;
  _cachedStartImage = null;
  _cachedStickyImage = null;
  _cachedBoomBar = null;
  _cachedBeforeWrapperItems = null;
  _cachedFixedAboveElements = null;
  _cachedMegaMenus = null;
  _cachedDesktopContainer = null;
  _cachedTabletContainer = null;

  // Pre-parsed breakpoint values
  _parsedDesktopBreakpoint = 0;
  _parsedTabletBreakpoint = 0;

  // rAF scroll throttle state
  _rafPending = false;

  // Stored bound handlers for cleanup
  _boundUpdateSticky = null;
  _boundUpdateMobileBreakpoint = null;

  // Last --fixed-nav-top value to avoid redundant writes
  _lastFixedNavTop = null;

  constructor(element, options = {}) {
    this.root = typeof element === "string"
      ? document.querySelector(element)
      : element;

    this.activeHeaderContainer = this.root.querySelector(".wp-block-kadence-header-desktop");
    this.rootID = "aaa";

    this.autoTransparentSpacing = "1" === this.root.dataset?.autoTransparentSpacing;
    this.sticky = "1" == this.root.dataset?.sticky;
    this.stickyTablet = "1" == this.root.dataset?.stickyTablet;
    this.stickyMobile = "1" == this.root.dataset?.stickyMobile;
    this.transparent = "1" == this.root.dataset?.transparent;
    this.transparentTablet = "1" == this.root.dataset?.transparentTablet;
    this.transparentMobile = "1" == this.root.dataset?.transparentMobile;
    this.stickySection = this.root.dataset?.stickySection;
    this.stickySectionTablet = this.root.dataset?.stickySectionTablet;
    this.stickySectionMobile = this.root.dataset?.stickySectionMobile;
    this.shrinkMain = "1" === this.root.dataset?.shrinkMain;
    this.shrinkMainHeight = this.root.dataset?.shrinkMainHeight;
    this.shrinkMainHeightTablet = this.root.dataset?.shrinkMainHeightTablet;
    this.shrinkMainHeightMobile = this.root.dataset?.shrinkMainHeightMobile;
    this.revealScrollUp = "1" === this.root.dataset?.revealScrollUp;
    this.mobileBreakpoint = this.root.dataset?.mobileBreakpoint;

    // Pre-parse breakpoint values once
    const config = getHeaderConfig();
    this._parsedDesktopBreakpoint = this.mobileBreakpoint && 0 !== this.mobileBreakpoint
      ? parseInt(this.mobileBreakpoint, 10)
      : parseInt(config.breakPoints.desktop, 10);
    this._parsedTabletBreakpoint = parseInt(config.breakPoints.tablet, 10);

    // Cache static DOM references
    this._cachedSiteBlocks = document.getElementsByClassName("wp-site-blocks")?.[0] || null;
    // this._cachedBreakpointEl = document.querySelector('.entry-content.single-content > *:nth-child(2)');
    this._cachedBreakpointEl = document.querySelector('body');
    this._cachedAdminNav = document.querySelector('#wpadminbar');
    this._cachedBoomBar = document.querySelector(".boom_bar");
    this._cachedBeforeWrapperItems = document.querySelectorAll(".kadence-before-wrapper-item");
    this._cachedFixedAboveElements = document.querySelectorAll(".kadence-pro-fixed-above");
    this._cachedDesktopContainer = this.root.querySelector(".wp-block-kadence-header-desktop");
    this._cachedTabletContainer = this.root.querySelector(".wp-block-kadence-header-tablet");

    // Create bound handlers once (allows removeEventListener if needed)
    this._boundUpdateSticky = this._onStickyEvent.bind(this);
    this._boundUpdateMobileBreakpoint = this.updateMobileBreakpoint.bind(this);

    this._state = "CREATED";

    if (this.sticky || this.stickyTablet || this.stickyMobile) {
      this.initStickyHeader();
    }

    if (this.mobileBreakpoint && 0 !== this.mobileBreakpoint) {
      this.initMobileBreakpoint();
    }

    var mountedEvent = new Event("MOUNTED", { bubbles: true });
    mountedEvent.ID = this.rootID;
    this.root.dispatchEvent(mountedEvent);

    this._state = "IDLE";
  }

  // ---- responsive desktop / tablet switching --------------------------------

  initMobileBreakpoint() {
    if (this.mobileBreakpoint && 0 !== this.mobileBreakpoint) {
      window.addEventListener("resize", this._boundUpdateMobileBreakpoint, false);
      window.addEventListener("hashchange", this._boundUpdateMobileBreakpoint, false);
      window.addEventListener("load", this._boundUpdateMobileBreakpoint, false);
      window.addEventListener("orientationchange", this._boundUpdateMobileBreakpoint);
    }
  }

  updateMobileBreakpoint() {
    this.setActiveSize();

    if ("desktop" == this.activeSize) {
      this._cachedTabletContainer.style.display = "none";
      this._cachedDesktopContainer.style.display = "block";
    } else {
      this._cachedDesktopContainer.style.display = "none";
      this._cachedTabletContainer.style.display = "block";
    }

    // Invalidate image caches when active container changes
    this._cachedStartImage = null;
    this._cachedStickyImage = null;
    this._cachedMegaMenus = null;
  }

  // ---- sticky header init ---------------------------------------------------

  initStickyHeader() {
    if (this.sticky) {
      this.createAndSetPlaceholderAndStickyWrappers("desktop");
    }

    if (this.stickyTablet || this.stickyMobile) {
      this.createAndSetPlaceholderAndStickyWrappers("tablet");
    }

    if (this.placeholderWrapper && this.stickyWrapper) {
      this.setActiveSize();
      this.updatePlaceholderAndStickyWrappers();

      if ("desktop" == this.activeSize) {
        if (this.sticky) {
          this.activeOffsetTop = this.getOffset(this.placeholderWrapper).top;
        }
      } else if ("tablet" == this.activeSize) {
        if (this.stickyTablet) {
          this.activeOffsetTop = this.getOffset(this.placeholderWrapper).top;
        }
      } else {
        if (this.stickyMobile) {
          this.activeOffsetTop = this.getOffset(this.placeholderWrapper).top;
        }
      }

      window.addEventListener("resize", this._boundUpdateSticky, false);
      window.addEventListener("hashchange", this._boundUpdateSticky, false);
      window.addEventListener("scroll", this._boundUpdateSticky, { passive: true });
      window.addEventListener("load", this._boundUpdateSticky, false);
      window.addEventListener("orientationchange", this._boundUpdateSticky);

      if ("complete" === document.readyState) {
        this.updateSticky("updateActive");
      }

      if (
        document.body.classList.contains("woocommerce-demo-store") &&
        document.body.classList.contains("kadence-store-notice-placement-above")
      ) {
        this.respondToVisibility(
          document.querySelector(".woocommerce-store-notice"),
          () => {
            // Fixed: original Kadence code called .bind(this) on the void return
            this.updateSticky("updateActive");
          }
        );
      }
    }
  }

  // ---- helpers --------------------------------------------------------------

  setActiveSize() {
    if (this._parsedDesktopBreakpoint < window.innerWidth) {
      this.activeSize = "desktop";
    } else if (this._parsedTabletBreakpoint < window.innerWidth) {
      this.activeSize = "tablet";
    } else {
      this.activeSize = "mobile";
    }

    this.activeHeaderContainer = "desktop" == this.activeSize
      ? this._cachedDesktopContainer
      : this._cachedTabletContainer;
  }

  createAndSetPlaceholderAndStickyWrappers(size) {
    const containerSelector = "desktop" == size
      ? ".wp-block-kadence-header-desktop"
      : ".wp-block-kadence-header-tablet";

    const section = this["stickySection" + this.activeSizeCased(size)];

    if ("top" == section || "bottom" == section || "main" == section) {
      var rowSelector = "";

      if ("top" == section) {
        rowSelector = ".wp-block-kadence-header-row-top";
      } else if ("bottom" == section) {
        rowSelector = ".wp-block-kadence-header-row-bottom";
      } else {
        rowSelector = ".wp-block-kadence-header-row-center";
      }

      this.stickyWrapper = this.root.querySelector(containerSelector + " " + rowSelector);
      this.placeholderWrapper = this.wrap([this.stickyWrapper]);
    } else if ("top_main" == section || "bottom_main" == section) {
      var elements = [];

      if ("top_main" == section) {
        elements.push(this.root.querySelector(containerSelector + " .wp-block-kadence-header-row-top"));
        elements.push(this.root.querySelector(containerSelector + " .wp-block-kadence-header-row-center"));
      }

      if ("bottom_main" == section) {
        elements.push(this.root.querySelector(containerSelector + " .wp-block-kadence-header-row-center"));
        elements.push(this.root.querySelector(containerSelector + " .wp-block-kadence-header-row-bottom"));
      }

      this.stickyWrapper = this.wrap(elements);
      this.placeholderWrapper = this.wrap([this.stickyWrapper]);
    } else {
      this.stickyWrapper = this.root.querySelector(containerSelector);
      this.placeholderWrapper = this.root;
    }

    if (this.stickyWrapper && this.placeholderWrapper) {
      this.stickyWrapper.classList.add("kb-header-sticky-wrapper");
      this.placeholderWrapper.classList.add("kb-header-placeholder-wrapper");
    }
  }

  updatePlaceholderAndStickyWrappers() {
    const section = this["stickySection" + this.activeSizeCased()];

    if (section) {
      this.stickyWrapper = this.activeHeaderContainer.querySelector(".kb-header-sticky-wrapper");
      this.placeholderWrapper = this.activeHeaderContainer.querySelector(".kb-header-placeholder-wrapper");
    } else {
      this.stickyWrapper = this.activeHeaderContainer;
      this.placeholderWrapper = this.root;
    }
  }

  wrap(children, wrapper) {
    wrapper = wrapper || document.createElement("div");

    if (children && children[0]) {
      children[0].parentNode.insertBefore(wrapper, children[0]);
      children.forEach((child) => {
        wrapper.appendChild(child);
      });
    }

    return wrapper;
  }

  respondToVisibility(element, callback) {
    var options = {
      root: document.documentElement,
    };

    var observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        callback(entry.intersectionRatio > 0);
      });
    }, options);

    observer.observe(element);
  }

  // ---- rAF-throttled scroll entry point ------------------------------------

  _onStickyEvent(eventOrAction) {
    // For non-scroll events (resize, hashchange, load, orientationchange,
    // or the direct "updateActive" string call), run immediately.
    if (typeof eventOrAction === 'string' || (eventOrAction && eventOrAction.type !== 'scroll')) {
      this.updateSticky(eventOrAction);
      return;
    }

    // For scroll events, coalesce into a single rAF callback.
    if (this._rafPending) return;
    this._rafPending = true;

    requestAnimationFrame(() => {
      this._rafPending = false;
      this.updateSticky(eventOrAction);
    });
  }

  // ---- main sticky loop -----------------------------------------------------

  updateSticky(eventOrAction) {
    if (!this.stickyWrapper) {
      return;
    }

    // ── BATCH ALL DOM READS FIRST ──────────────────────────────────────────
    const scrollY = window.scrollY;
    const siteBlocks = this._cachedSiteBlocks;
    const siteBlocksTop = this.getOffset(siteBlocks).top;
    var topOffset = siteBlocksTop;

    this.anchorOffset = this.getOffset(this.placeholderWrapper).top;

    // NMTC: use the content-based breakpoint when the element exists,
    // otherwise fall back to the default anchor-based offset.
    if (this._cachedBreakpointEl) {
      this.anchorOffset = this.getOffset(this._cachedBreakpointEl).top - 300;
    }

    this.setActiveSize();
    this.activeOffsetTop = this.getOffset(this.stickyWrapper).top;

    // If sticky is not enabled for the current active size, clean up and bail out
    if (
      !(
        ("desktop" === this.activeSize && this.sticky) ||
        ("tablet" === this.activeSize && this.stickyTablet) ||
        ("mobile" === this.activeSize && this.stickyMobile)
      )
    ) {
      this.stickyWrapper.classList.remove("item-is-fixed");
      this.stickyWrapper.classList.remove("item-at-start");
      this.stickyWrapper.classList.remove("item-is-stuck");
      this.stickyWrapper.style.height = null;
      this.stickyWrapper.style.top = null;
      this.stickyWrapper.style.position = "initial";
      // Fixed: original Kadence code had (void 0).classList which always throws
      this.stickyWrapper.parentNode?.classList?.remove("child-is-fixed");
      document.body.classList.remove("header-is-fixed");
      return;
    }

    this.updatePlaceholderAndStickyWrappers();

    // Read geometry before any writes
    var stickyHeight = this.stickyWrapper.offsetHeight;
    var placeholderWidth = this.placeholderWrapper.offsetWidth;
    var placeholderLeft = this.getOffset(this.placeholderWrapper).left;

    // Adjust topOffset for boom bar (classList.contains instead of toString().includes())
    if (document.body.classList.contains("boom_bar-static-top") && this._cachedBoomBar) {
      topOffset = siteBlocksTop - this._cachedBoomBar.offsetHeight;
    }

    // Adjust for before-wrapper items
    if (this._cachedBeforeWrapperItems.length) {
      var beforeWrapperHeight = 0;
      for (let i = 0; i < this._cachedBeforeWrapperItems.length; i++) {
        beforeWrapperHeight += this._cachedBeforeWrapperItems[i].offsetHeight;
      }
      topOffset = siteBlocksTop - beforeWrapperHeight;
    }

    // Adjust for fixed-above elements
    if (this._cachedFixedAboveElements.length) {
      var fixedAboveHeight = 0;
      for (let i = 0; i < this._cachedFixedAboveElements.length; i++) {
        fixedAboveHeight += this._cachedFixedAboveElements[i].offsetHeight;
      }
      topOffset = siteBlocksTop + fixedAboveHeight;
    }

    // Read mega menu offset (geometry read before writes)
    if (!this._cachedMegaMenus) {
      this._cachedMegaMenus = this.stickyWrapper.querySelectorAll(
        ".kadence-menu-mega-enabled > .kb-nav-sub-menu"
      );
    }
    var megaMenuTop = this._cachedMegaMenus.length
      ? this.getOffset(this._cachedMegaMenus[0]).top
      : 0;

    // Read shrink dimensions if needed
    var shrinkHeight = 0;
    var rowInner = null;
    var shrinkImages = null;
    var calculatedHeight = 0;
    if (this.shrinkMain) {
      shrinkHeight =
        "mobile" === this.activeSize
          ? this.shrinkMainHeightMobile
          : "tablet" === this.activeSize
            ? this.shrinkMainHeightTablet
            : this.shrinkMainHeight;

      if (shrinkHeight) {
        shrinkImages = this.activeHeaderContainer.querySelectorAll(
          ".kb-img, .wp-block-kadence-identity img, .wp-block-image img"
        );
        rowInner = this.activeHeaderContainer.querySelector(
          ".wp-block-kadence-header-row-center .kadence-header-row-inner"
        );

        if (
          !this.shrinkStartHeight ||
          (eventOrAction && eventOrAction.type !== undefined && "orientationchange" === eventOrAction.type)
        ) {
          this.shrinkStartHeight = rowInner.offsetHeight;
        }

        calculatedHeight = Math.max(shrinkHeight, this.shrinkStartHeight - scrollY);
      }
    }

    const finalTopOffset = topOffset;
    const topOffsetMinusHeight = topOffset - stickyHeight;
    const currentTopPlusHeight = this.currentTopPosition + stickyHeight;
    var parentNode = this.stickyWrapper.parentNode;

    // ── ALL READS DONE — NOW BATCH WRITES ──────────────────────────────────

    if (eventOrAction && "updateActive" === eventOrAction) {
      this.stickyWrapper.style.top = "auto";
    }

    // Set placeholder height if not transparent
    const sizeSuffix = this.activeSizeCased();
    if (!this["transparent" + sizeSuffix]) {
      this.placeholderWrapper.style.height = stickyHeight + "px";
    }

    // Set placeholder height if has a sticky section and is transparent
    if ("" !== this["stickySection" + sizeSuffix] && this["transparent" + sizeSuffix]) {
      this.placeholderWrapper.style.height = stickyHeight + "px";
    }

    // Write mega menu offset
    if (this._cachedMegaMenus.length) {
      this.stickyWrapper.style.setProperty(
        "--kb-sticky-mega-overflow-header-offset",
        megaMenuTop - scrollY + "px"
      );
    }

    // Write shrink main header
    if (this.shrinkMain && shrinkHeight && rowInner) {
      rowInner.style.height = calculatedHeight + "px";
      rowInner.style.minHeight = calculatedHeight + "px";
      rowInner.style.maxHeight = calculatedHeight + "px";

      if (scrollY == 0) {
        if (shrinkImages) {
          for (let i = 0; i < shrinkImages.length; i++) {
            shrinkImages[i].style.maxHeight = "100%";
          }
        }
      } else if (shrinkImages) {
        for (let i = 0; i < shrinkImages.length; i++) {
          const img = shrinkImages[i];
          if (!img.closest(".mega-menu")) {
            img.style.maxHeight = calculatedHeight + "px";
          }
        }
      }
    }

    // Apply or remove fixed positioning based on sticking state
    if (this.isSticking) {
      this.stickyWrapper.style.position = "fixed";
      this.stickyWrapper.style.width = placeholderWidth + "px";
      this.stickyWrapper.style.left = placeholderLeft + "px";
      this.stickyWrapper.style.top = topOffset + "px";
    } else {
      this.stickyWrapper.style.position = "initial";
      this.stickyWrapper.style.width = "initial";
      this.stickyWrapper.style.left = "initial";
      this.stickyWrapper.style.top = "initial";
    }

    // Determine sticky state based on scroll position
    if (this.revealScrollUp) {
      var isScrollingDown = scrollY > this.lastScrollTop;
      var anchorThreshold = Math.floor(this.anchorOffset + stickyHeight);

      if (scrollY <= this.anchorOffset - topOffset) {
        this.currentTopPosition = 0;
        this.setStickyChanged(false);
      } else if (scrollY <= anchorThreshold) {
        if (isScrollingDown) {
          this.currentTopPosition = 0;
          this.setStickyChanged(false);
        } else {
          this.stickyWrapper.classList.remove("item-hidden-above");
          this.currentTopPosition = finalTopOffset;
          this.setStickyChanged(true);
        }
      } else if (scrollY >= this.currentTopPosition && scrollY <= currentTopPlusHeight) {
        this.setStickyChanged(false);
      } else {
        if (isScrollingDown) {
          this.stickyWrapper.classList.add("item-hidden-above");
          this.stickyWrapper.style.top = topOffsetMinusHeight + "px";
          this.currentTopPosition = topOffsetMinusHeight;
        } else {
          this.stickyWrapper.classList.remove("item-hidden-above");
          this.currentTopPosition = finalTopOffset;
        }
        this.setStickyChanged(true);
      }
    } else {
      var anchorThreshold = Math.floor(this.anchorOffset - topOffset);

      if (scrollY <= anchorThreshold) {
        this.currentTopPosition = 0;
        this.setStickyChanged(false);
      } else {
        this.currentTopPosition = finalTopOffset;
        this.setStickyChanged(true);
      }
    }

    this.lastScrollTop = scrollY;

    // Update CSS classes based on scroll position relative to anchor threshold
    if (scrollY <= anchorThreshold) {
      this.stickyWrapper.classList.add("item-is-fixed");
      this.stickyWrapper.classList.add("item-at-start");
      this.stickyWrapper.classList.remove("item-is-stuck");
      parentNode.classList.add("child-is-fixed");
      document.body.classList.add("header-is-fixed");
    } else if (scrollY > anchorThreshold) {
      if (this.revealScrollUp) {
        if (scrollY < stickyHeight + 60 && this.stickyWrapper.classList.contains("item-at-start")) {
          this.stickyWrapper.style.height = null;
          this.stickyWrapper.classList.add("item-is-fixed");
          this.stickyWrapper.classList.add("item-is-stuck");
          parentNode.classList.add("child-is-fixed");
          document.body.classList.add("header-is-fixed");
        } else {
          this.stickyWrapper.classList.add("item-is-fixed");
          this.stickyWrapper.classList.add("item-is-stuck");
          this.stickyWrapper.classList.remove("item-at-start");
          parentNode.classList.add("child-is-fixed");
          document.body.classList.add("header-is-fixed");
        }
      } else {
        this.stickyWrapper.classList.add("item-is-fixed");
        this.stickyWrapper.classList.remove("item-at-start");
        this.stickyWrapper.classList.add("item-is-stuck");
        parentNode.classList.add("child-is-fixed");
        document.body.classList.add("header-is-fixed");
      }
    }
    // else if (this.stickyWrapper.classList.contains("item-is-fixed")) {
    //   this.stickyWrapper.classList.remove("item-is-fixed");
    //   this.stickyWrapper.classList.remove("item-at-start");
    //   this.stickyWrapper.classList.remove("item-is-stuck");
    //   this.stickyWrapper.style.position = "initial";
    //   this.stickyWrapper.style.width = "initial";
    //   this.stickyWrapper.style.left = "initial";
    //   this.stickyWrapper.style.top = "initial";
    //   this.stickyWrapper.style.height = null;
    //   parentNode.classList.remove("child-is-fixed");
    //   document.body.classList.remove("header-is-fixed");
    // }

    // NMTC: manage --fixed-nav-top CSS variable (admin bar offset with smooth slide-in)
    let computedNavTop = this._cachedAdminNav ? this._cachedAdminNav.clientHeight : 0;

    if (scrollY >= anchorThreshold) {
      // Within 4× the header height of the breakpoint, smoothly slide the
      // offset from roughly -2× headerHeight up to the full admin-bar height.
      if (scrollY < anchorThreshold + stickyHeight * 4) {
        computedNavTop += (scrollY - (anchorThreshold + stickyHeight * 4)) / 2;
      }
    }

    // Only write the CSS variable when the value actually changes
    const navTopValue = `${computedNavTop}px`;
    if (this._lastFixedNavTop !== navTopValue) {
      this._lastFixedNavTop = navTopValue;
      document.documentElement.style.setProperty("--fixed-nav-top", navTopValue);
    }
  }

  // ---- utility methods ------------------------------------------------------

  activeSizeCased(size) {
    const activeSize = size ? size : this.activeSize;

    if ("desktop" === activeSize) {
      return "";
    }

    return activeSize.charAt(0).toUpperCase() + activeSize.slice(1);
  }

  setStickyChanged(sticking) {
    if (this.isSticking != sticking) {
      this.isSticking = sticking;
      var event = new Event("KADENCE_HEADER_STICKY_CHANGED", { bubbles: true });
      event.isSticking = this.isSticking;
      this.root.dispatchEvent(event);
    }
  }

  setTransparentChanged(transparent) {
    if (this.isTransparent != transparent) {
      this.isTransparent = transparent;
      var event = new Event("KADENCE_HEADER_STICKY_CHANGED", { bubbles: true });
      event.isTransparent = this.isTransparent;
      this.root.dispatchEvent(event);
    }
  }

  getOffset(element) {
    if (element instanceof HTMLElement) {
      var rect = element.getBoundingClientRect();
      return {
        top: rect.top + window.pageYOffset,
        left: rect.left + window.pageXOffset,
      };
    }

    return {
      top: null,
      left: null,
    };
  }

  get state() {
    return this._state;
  }

  set state(value) {
    this._state = value;
    var event = new Event("STATE");
    event.val = value;
    event.ID = this.rootID;
    this.root.dispatchEvent(event);
  }
}

window.KBHeader = KBHeader;

const initKBHeader = () => {
  window.KBHeaderBlocks = [];
  var headers = document.querySelectorAll(".wp-block-kadence-header");

  for (let i = 0; i < headers.length; i++) {
    var headerElement = headers[i];
    const headerInstance = new KBHeader(headerElement);
    window.KBHeaderBlocks.push(headerInstance);
  }
};

if ("loading" === document.readyState) {
  document.addEventListener("DOMContentLoaded", initKBHeader);
} else {
  initKBHeader();
}

// ---------------------------------------------------------------------------
// activateFixedNav — sets the initial --fixed-nav-top value based on the
// admin bar height.  The scroll-dependent smooth slide-in is now handled
// inside KBHeader.updateSticky so it shares the same breakpoint.
// ---------------------------------------------------------------------------
export function activateFixedNav() {
  const adminNav = document.querySelector('#wpadminbar');
  const adminNavHeight = adminNav ? adminNav.clientHeight : 0;
  document.documentElement.style.setProperty("--fixed-nav-top", `${adminNavHeight}px`);
}

// ---------------------------------------------------------------------------
// KBStickyImage — handles swapping between standard, sticky, and transparent
// images within Kadence image and identity blocks based on header sticky state.
// ---------------------------------------------------------------------------

class KBStickyImage {
  constructor(element, options = {}) {
    this.root = typeof element === "string"
      ? document.querySelector(element)
      : element;

    this._state = "CREATED";

    this.isLogo = this.root.classList.contains("kb-identity");

    this.standardElem = this.isLogo
      ? this.root.querySelector(".custom-logo")
      : this.root.querySelector(".kb-img:not(.kb-img-sticky):not(.kb-img-transparent)");

    this.stickyElem = this.root.querySelector(".kb-img-sticky");
    this.transparentElem = this.root.querySelector(".kb-img-transparent");

    this.isSticking = false;

    const headerBlock = this.root.closest(".wp-block-kadence-header");
    this.isTransparentDesktop = headerBlock?.classList?.contains("header-desktop-transparent");
    this.isTransparentTablet = headerBlock?.classList?.contains("header-tablet-transparent");
    this.isTransparentMobile = headerBlock?.classList?.contains("header-mobile-transparent");

    if (this.stickyElem) {
      this.initStickyTracking();
    }

    this.emitEvent("MOUNTED");
    this._state = "IDLE";
  }

  initStickyTracking() {
    window.addEventListener("KADENCE_HEADER_STICKY_CHANGED", this.toggleSticky.bind(this));
  }

  toggleSticky(event) {
    const isSticking = event.isSticking;
    this.isSticking = isSticking;

    if (isSticking) {
      this.setStickyStyles();
    } else {
      this.setStandardStyles();
    }
  }

  setStickyStyles() {
    if (this.stickyElem) {
      this.stickyElem.style.display = "initial";
    }
    if (this.standardElem) {
      this.standardElem.style.display = "none";
    }
    if (this.transparentElem) {
      this.transparentElem.style.display = "none";
    }
  }

  setStandardStyles() {
    const activeSize = this.getActiveSize();
    const isTransparent =
      ("desktop" === activeSize && this.isTransparentDesktop) ||
      ("tablet" === activeSize && this.isTransparentTablet) ||
      ("mobile" === activeSize && this.isTransparentMobile);

    if (this.stickyElem) {
      this.stickyElem.style.display = "none";
    }

    if (this.transparentElem && !this.isLogo) {
      this.transparentElem.style.display = "none";
    }

    if (isTransparent) {
      if (this.transparentElem) {
        this.transparentElem.style.display = "initial";
      }
    } else {
      if (this.standardElem) {
        this.standardElem.style.display = "initial";
      }
    }
  }

  getActiveSize() {
    const config = getHeaderConfig();
    if (parseInt(config.breakPoints.desktop, 10) < window.innerWidth) {
      return "desktop";
    }
    return parseInt(config.breakPoints.tablet, 10) < window.innerWidth ? "tablet" : "mobile";
  }

  get state() {
    return this._state;
  }

  set state(value) {
    this._state = value;
    this.emitEvent("STATE", { val: value });
  }

  emitEvent(name, detail = {}) {
    const event = new CustomEvent(name, { bubbles: true, detail });
    this.root.dispatchEvent(event);
  }
}

window.KBStickyImage = KBStickyImage;

function initKBImages() {
  const elements = document.querySelectorAll(".wp-block-kadence-image, .wp-block-kadence-identity");
  window.KBStickyBlocks = Array.from(elements).map((el) => new KBStickyImage(el));
}

if ("loading" === document.readyState) {
  document.addEventListener("DOMContentLoaded", initKBImages);
} else {
  initKBImages();
}

// ---------------------------------------------------------------------------
// Kadence Navigation — handles dropdown toggles, mega menus, keyboard nav,
// edge detection, scroll spy, and orientation switching.
// ---------------------------------------------------------------------------

(function () {
  /**
   * Get element offset including page scroll
   */
  const getOffset = function (element) {
    if (element instanceof HTMLElement) {
      var rect = element.getBoundingClientRect();
      return {
        top: rect.top + window.pageYOffset,
        left: rect.left + window.pageXOffset,
        right: rect.right + window.pageXOffset,
      };
    }
    return { top: null, left: null, right: null };
  };

  /**
   * Toggle a menu item's dropdown open/closed
   * @param {HTMLElement} menuItem - The menu item to toggle
   * @param {boolean|null} forceState - Force open (true) or close (false), or toggle (null)
   * @param {boolean} isVertical - Whether the nav is in vertical orientation
   * @returns {boolean} - The new open state
   */
  const toggleMenuItem = function (menuItem, forceState = null, isVertical = false) {
    const toggleBtn = menuItem.querySelector(".kb-nav-dropdown-toggle-btn");
    const subMenu = menuItem.querySelector("ul.sub-menu");
    let isOpen = menuItem.classList.contains("menu-item--toggled-on");

    // If forceState is provided, use it
    if (forceState !== undefined && typeof forceState === "boolean") {
      isOpen = !forceState;
    }

    const willOpen = !isOpen;

    if (toggleBtn) {
      toggleBtn.setAttribute("aria-expanded", willOpen.toString());
    }

    // Safari workaround: toggle .menu-open on the sticky header wrapper
    // because Safari's :has() style invalidation is broken
    const stickyWrapper = menuItem.closest(".item-at-start, .item-is-stuck, .kb-header-container");

    const updateMenuOpenClass = () => {
      if (stickyWrapper) {
        const anyMegaOpen = stickyWrapper.querySelector(
          ".kb-menu-mega-width-full.menu-item--toggled-on, .kb-menu-mega-width-content.menu-item--toggled-on, .kadence-menu-mega-enabled.menu-item--toggled-on"
        );
        stickyWrapper.classList.toggle("menu-open", !!anyMegaOpen);
      }
    };

    if (isOpen) {
      // Close the menu
      setTimeout(function () {
        menuItem.classList.remove("menu-item--toggled-on");
        subMenu.classList.remove("toggle-show");
        updateMenuOpenClass();
      }, 5);

      // Also close any nested open menus
      var nestedOpen = menuItem.querySelectorAll(".menu-item--toggled-on");
      for (let i = 0; i < nestedOpen.length; i++) {
        toggleMenuItem(nestedOpen[i], false);
      }
    } else {
      // Open the menu
      if (!isVertical) {
        // Close sibling menus first
        var siblingOpen = menuItem.parentNode.querySelectorAll("li.menu-item--toggled-on");
        for (let i = 0; i < siblingOpen.length; i++) {
          toggleMenuItem(siblingOpen[i], false);
        }
      }

      // Mega menu position is cached in CSS vars and only refreshed on
      // window resize. Layout can shift after that (webfonts, images,
      // sticky header) without firing a resize event, leaving the menu
      // anchored to a stale toggle position — recompute right before it
      // opens so it's always aligned to current geometry.
      if (menuItem.classList.contains("kadence-menu-mega-enabled")) {
        updateFullWidthMegaMenus();
        updateContentWidthMegaMenus();
      }

      menuItem.classList.add("menu-item--toggled-on");
      subMenu.classList.add("toggle-show");
      updateMenuOpenClass();
    }

    return willOpen;
  };

  /**
   * Handle sub-menu edge detection (prevent overflow off screen)
   */
  const initSubMenuEdgeDetection = function (nav) {
    var menuItemsWithChildren = nav.querySelectorAll(".menu-item-has-children");
    if (menuItemsWithChildren.length) {
      for (let i = 0; i < menuItemsWithChildren.length; i++) {
        menuItemsWithChildren[i].addEventListener("mouseenter", function () {
          var subMenu = menuItemsWithChildren[i].querySelector("ul.sub-menu");
          if (subMenu) {
            // Clear previous edge flags before re-measuring, otherwise a bad
            // reading (e.g. taken while the page was still reflowing) sticks
            // forever since these classes were only ever added, never removed.
            subMenu.classList.remove("sub-menu-right-edge", "sub-menu-left-edge");
            var offset = getOffset(subMenu);
            var left = offset.left;
            var right = offset.right;
            var width = subMenu.offsetWidth;
            var windowWidth = window.innerWidth;

            if (left + width > windowWidth) {
              subMenu.classList.add("sub-menu-right-edge");
            }
            if (right - width < 0) {
              subMenu.classList.add("sub-menu-left-edge");
            }
          }
        });
      }
    }
  };

  /**
   * Close menus when clicking outside
   */
  const initClickOutsideClose = function () {
    document.addEventListener("click", function (event) {
      const openMenus = document.querySelectorAll(".menu-item--toggled-on");
      openMenus.forEach((menuItem) => {
        // Don't close if clicking a .toggle-first-mega trigger (handled separately)
        if (event.target.closest(".toggle-first-mega")) return;
        if (!menuItem.contains(event.target)) {
          toggleMenuItem(menuItem, false);
        }
      });
    });
  };

  /**
   * Close mega menus when scrolling
   */
  const initScrollToClose = function () {
    let lastScrollY = window.scrollY;
    let scrollThreshold = 50; // pixels of scroll before closing

    window.addEventListener("scroll", function () {
      const openMegaMenus = document.querySelectorAll(
        ".kb-menu-mega-width-full.menu-item--toggled-on, .kb-menu-mega-width-content.menu-item--toggled-on, .kadence-menu-mega-enabled.menu-item--toggled-on"
      );

      // Don't close mega menus if a search/input field inside one is focused
      // (on tablets, focusing an input can trigger a scroll from viewport resize)
      const focused = document.activeElement;
      const focusInMega = focused && (focused.tagName === 'INPUT' || focused.tagName === 'TEXTAREA') &&
        focused.closest('.menu-item--toggled-on');

      if (openMegaMenus.length && !focusInMega && Math.abs(window.scrollY - lastScrollY) > scrollThreshold) {
        openMegaMenus.forEach((menuItem) => {
          toggleMenuItem(menuItem, false);
        });
        lastScrollY = window.scrollY;
      }

      // Update lastScrollY when no menus are open
      if (!openMegaMenus.length) {
        lastScrollY = window.scrollY;
      }
    }, { passive: true });
  };

  /**
   * Initialize dropdown toggle buttons and keyboard navigation
   */
  const initDropdownToggles = function (nav) {
    const subMenus = nav.querySelectorAll(".kb-navigation .sub-menu");

    if (subMenus.length) {
      for (let i = 0; i < subMenus.length; i++) {
        const parentItem = subMenus[i].parentNode;
        const toggleBtn = parentItem.querySelector(".kb-nav-dropdown-toggle-btn");

        // Toggle button click
        toggleBtn.addEventListener("click", function (event) {
          event.preventDefault();
          toggleMenuItem(parentItem, null, nav.classList.contains("is-vertical"));
        });

        // Sub-click mode: clicking the link text also toggles
        if (parentItem.classList.contains("kb-nav-link-sub-click")) {
          const linkContent = parentItem.querySelector(":scope > .kb-link-wrap > .kb-nav-link-content");
          if (linkContent) {
            linkContent.addEventListener("click", function (event) {
              event.preventDefault();
              toggleMenuItem(parentItem, null, nav.classList.contains("is-vertical"));
            });
          }
        }

        // Mouse leave closes dropdown (unless vertical or sub-click mode)
        parentItem.addEventListener("mouseleave", function (event) {
          if (!nav.classList.contains("is-vertical") && !event.target.classList.contains("kb-nav-link-sub-click")) {
            toggleMenuItem(event.target, false);
          }
        });

        // Focus handling - close sibling menus when tabbing
        const parentLink = parentItem.querySelector("a");
        if (parentLink) {
          parentLink.addEventListener("focus", function (event) {
            if (!nav.classList.contains("is-vertical")) {
              var siblingOpen = event.target.parentNode.parentNode.querySelectorAll("li.menu-item--toggled-on");
              for (let j = 0; j < siblingOpen.length; j++) {
                if (parentItem !== siblingOpen[j]) {
                  toggleMenuItem(siblingOpen[j], false);
                }
              }
            }
          });
        }

        // Keyboard navigation within sub-menu
        subMenus[i].addEventListener("keydown", function (event) {
          if (!nav.classList.contains("is-vertical")) {
            // Tab key handling
            if (event.keyCode === 9) {
              var focusableSelector = "ul.toggle-show > li > .kb-link-wrap > a, ul.toggle-show > li > .kb-link-wrap > .kb-nav-dropdown-toggle-btn";

              if (parentItem.classList.contains("kadence-menu-mega-enabled")) {
                focusableSelector = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex="0"], [contenteditable]';
              }

              var focusables = Array.from(subMenus[i].querySelectorAll(focusableSelector));

              if (event.shiftKey) {
                // Shift+Tab from first element closes menu
                if (document.activeElement === focusables[0]) {
                  toggleMenuItem(parentItem, false);
                }
              } else {
                // Tab from last element closes menu and returns focus
                if (document.activeElement === focusables[focusables.length - 1]) {
                  toggleMenuItem(parentItem, false);
                  setTimeout(function () {
                    parentItem.querySelector(".kb-nav-dropdown-toggle-btn").focus();
                  }, 5);
                }
              }
            }

            // Escape key closes menu
            if (event.keyCode === 27) {
              toggleMenuItem(parentItem, false);
              parentItem.querySelector(".kb-nav-dropdown-toggle-btn").focus();
            }
          }
        });

        parentItem.classList.add("menu-item--has-toggle");
      }
    }
  };

  /**
   * Update navigation orientation (horizontal/vertical) based on breakpoints
   */
  const updateNavigationOrientation = function (navElements) {
    if (navElements.length) {
      for (let i = 0; i < navElements.length; i++) {
        const nav = navElements[i];
        var isVertical = false;

        if (window.innerWidth >= 1024) {
          if (nav.classList.contains("navigation-desktop-orientation-vertical")) {
            isVertical = true;
          }
        } else if (window.innerWidth >= 768) {
          if (nav.classList.contains("navigation-tablet-orientation-vertical")) {
            isVertical = true;
          }
        } else {
          if (nav.classList.contains("navigation-mobile-orientation-vertical")) {
            isVertical = true;
          }
        }

        if (isVertical) {
          nav.classList.remove("is-horizontal");
          nav.classList.add("is-vertical");
        } else {
          nav.classList.remove("is-vertical");
          nav.classList.add("is-horizontal");
        }
      }
    }
  };

  /**
   * Update full-width mega menu positioning
   */
  const updateFullWidthMegaMenus = function () {
    var subMenus = null;

    if (window.innerWidth > 1024) {
      subMenus = document.querySelectorAll(".wp-block-kadence-navigation.navigation-desktop-orientation-horizontal .kb-menu-mega-width-full > ul.sub-menu");
    } else if (window.innerWidth > 768) {
      subMenus = document.querySelectorAll(".wp-block-kadence-navigation.navigation-tablet-orientation-horizontal .kb-menu-mega-width-tablet-full > ul.sub-menu");
    } else {
      subMenus = document.querySelectorAll(".wp-block-kadence-navigation.navigation-mobile-orientation-horizontal .kb-menu-mega-width-mobile-full > ul.sub-menu");
    }

    for (let i = 0; i < subMenus.length; i++) {
      var parent = subMenus[i].parentNode;
      subMenus[i].style.setProperty("--kb-nav-dropdown-width", window.innerWidth + "px");
      subMenus[i].style.setProperty("--kb-nav-dropdown-show-left", -1 * Math.abs(parent.getBoundingClientRect().left) + "px");
      subMenus[i].style.setProperty("--kb-nav-dropdown-hide-transform-x", "0");
      subMenus[i].style.setProperty("--kb-nav-dropdown-show-transform-x", "0");
    }
  };

  /**
   * Update content-width mega menu positioning
   */
  const updateContentWidthMegaMenus = function () {
    var subMenus = null;

    if (window.innerWidth > 1024) {
      subMenus = document.querySelectorAll(".wp-block-kadence-navigation.navigation-desktop-orientation-horizontal .kb-menu-mega-width-content > ul.sub-menu");
    } else if (window.innerWidth > 768) {
      subMenus = document.querySelectorAll(".wp-block-kadence-navigation.navigation-tablet-orientation-horizontal .kb-menu-mega-width-tablet-content > ul.sub-menu");
    } else {
      subMenus = document.querySelectorAll(".wp-block-kadence-navigation.navigation-mobile-orientation-horizontal .kb-menu-mega-width-mobile-content > ul.sub-menu");
    }

    if (subMenus?.length) {
      for (let i = 0; i < subMenus.length; i++) {
        var parent = subMenus[i].parentNode;
        var rowInner = subMenus[i].closest(".kadence-header-row-inner");

        if (rowInner) {
          var styles = getComputedStyle(rowInner);
          var padding = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
          var leftOffset = parseFloat(rowInner.getBoundingClientRect().left) + parseFloat(styles.paddingLeft);

          subMenus[i].style.setProperty("--kb-nav-dropdown-width", rowInner.offsetWidth - padding + "px");
          subMenus[i].style.setProperty("--kb-nav-dropdown-show-left", -1 * Math.abs(parent.getBoundingClientRect().left - leftOffset) + "px");
        } else {
          subMenus[i].style.setProperty("--kb-nav-dropdown-width", "var(--wp--style--global--content-size, 100%)");
          subMenus[i].style.setProperty("--kb-nav-dropdown-show-left", "calc( (((100vw - var(--wp--style--global--content-size, 100%)) / 2) - " + Math.abs(parent.getBoundingClientRect().left) + "px))");
        }

        subMenus[i].style.setProperty("--kb-nav-dropdown-hide-transform-x", "0");
        subMenus[i].style.setProperty("--kb-nav-dropdown-show-transform-x", "0");
      }
    }
  };

  /**
   * Update current menu item based on hash
   */
  const updateCurrentMenuItem = function () {
    const menuItems = document.querySelectorAll(".menu-item");
    menuItems.forEach(function (item) {
      const link = item.querySelector("a");
      if (link?.href && link.href.includes("#")) {
        if (window.location.href === link.href) {
          item.classList.add("current-menu-item");
        } else {
          item.classList.remove("current-menu-item");
        }
      }
    });
  };

  // ---- Initialize navigation ------------------------------------------------

  (function initNavigation() {
    var navElements = document.querySelectorAll(".wp-block-kadence-navigation");
    if (!navElements.length) return;

    for (let i = 0; i < navElements.length; i++) {
      initDropdownToggles(navElements[i]);
      initSubMenuEdgeDetection(navElements[i]);
    }

    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateNavigationOrientation(navElements);
      }, 200);
    });

    updateNavigationOrientation(navElements);
    initClickOutsideClose();
    initScrollToClose();
  })();

  // ---- Initialize full-width mega menus -------------------------------------

  (function initFullWidthMegaMenus() {
    var subMenus = document.querySelectorAll(".wp-block-kadence-navigation .kb-menu-mega-width-full > ul.sub-menu");
    if (!subMenus.length) return;

    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateFullWidthMegaMenus();
      }, 200);
    });

    updateFullWidthMegaMenus();
  })();

  // ---- Initialize content-width mega menus ----------------------------------

  (function initContentWidthMegaMenus() {
    var subMenus = document.querySelectorAll(".wp-block-kadence-navigation .kb-menu-mega-width-content > ul.sub-menu");
    if (!subMenus.length) return;

    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateContentWidthMegaMenus();
      }, 200);
    });

    updateContentWidthMegaMenus();
  })();

  // ---- Auto-expand off-canvas menus with current item -----------------------

  (function initOffCanvasCurrentItem() {
    var menuItems = document.querySelectorAll(".kb-off-canvas-inner .wp-block-kadence-navigation .menu-item-has-children");
    if (menuItems.length) {
      for (let i = 0; i < menuItems.length; i++) {
        var currentItem = menuItems[i].querySelector(".current-menu-item");
        if (currentItem) {
          toggleMenuItem(menuItems[i], true);
        }
      }
    }
  })();

  // ---- Hash-based current menu item -----------------------------------------

  (function initHashCurrentItem() {
    if (window.location.hash !== "") {
      updateCurrentMenuItem();
    }
    window.onhashchange = function () {
      updateCurrentMenuItem();
    };
  })();

  // ---- Custom mega menu triggers (.toggle-first-mega) ----------------------

  (function initCustomMegaTriggers() {
    const megaParent = document.querySelector(
      ".kb-menu-mega-width-full, .kb-menu-mega-width-content, .kadence-menu-mega-enabled"
    );
    const megaSubmenu = megaParent?.querySelector("ul.sub-menu");

    if (!megaParent || !megaSubmenu) return;

    const nav = megaParent.closest(".wp-block-kadence-navigation");
    const isVertical = nav?.classList?.contains("is-vertical") || false;

    // Add aria-label to .search-button elements for accessibility
    document.querySelectorAll(".search-button a").forEach((btn) => {
      if (!btn.getAttribute("aria-label")) {
        btn.setAttribute("aria-label", "Open search");
      }
    });
    document.querySelectorAll(".kb-search-icon svg").forEach((btn) => {
      if (!btn.getAttribute("aria-label")) {
        btn.setAttribute("aria-label", "get search results");
      }
    });

    // Set up .toggle-first-mega triggers
    const triggers = document.querySelectorAll(".toggle-first-mega a");
    triggers.forEach((trigger) => {
      trigger.addEventListener("click", (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        evt.stopImmediatePropagation();

        const isOpen = toggleMenuItem(megaParent, null, isVertical);

        // If opened via search button, focus the search input
        const searchTrigger = trigger.closest(".search-button") || trigger;
        const isSearchTrigger = searchTrigger?.classList?.contains("search-button");

        if (isOpen && isSearchTrigger) {
          const searchInput = megaParent.querySelector("form.kb-search-form input");
          if (searchInput) {
            setTimeout(() => {
              searchInput.focus();
              if (searchInput.setSelectionRange) {
                const len = searchInput.value.length;
                searchInput.setSelectionRange(len, len);
              }
            }, 100);
          }
        }
      });
    });

    // Close mega menu when clicking anchor links within it
    const megaMenuLinks = document.querySelectorAll(".mega-menu .kb-nav-link-content");
    megaMenuLinks.forEach((link) => {
      link.addEventListener("click", (event) => {
        const anchor = event.currentTarget.closest("a[href]");
        if (!anchor) return;

        const url = new URL(anchor.href, window.location.href);
        const samePage = url.origin === window.location.origin && url.pathname === window.location.pathname;
        const isAnchorLink = samePage && url.hash;

        if (isAnchorLink) {
          toggleMenuItem(megaParent, false, isVertical);
        }
      });
    });
  })();

  // ---- Scroll spy integration -----------------------------------------------

  (function initScrollSpy() {
    if (typeof Gumshoe !== "function") return;

    var navElements = document.querySelectorAll('.wp-block-kadence-navigation[data-scroll-spy="1"]');
    if (!navElements.length) return;

    navElements.forEach(function (nav) {
      var offset = parseInt(nav.getAttribute("data-scroll-spy-offset"), 10);
      var headerRow = null;

      if (!offset) {
        headerRow = nav.closest(".wp-block-kadence-header-row");
      }

      const scrollSpyId = nav.dataset?.scrollSpyId;

      new Gumshoe('.wp-block-kadence-navigation[data-scroll-spy-id="' + scrollSpyId + '"] .kb-navigation .kb-nav-link-content', {
        nested: true,
        nestedClass: "current-menu-ancestor",
        navClass: "current-menu-item",
        offset: offset ? offset : headerRow ? function () { return headerRow?.getBoundingClientRect().height; } : 0,
      });
    });
  })();
})();
